// Implements: F-MCK-021
/**
 * "Personas con acceso" (mockup v3 :1651-1656): the owner first with the "Propietario" badge, then
 * every explicit assignment with its role. When `editable` (the owner), each row has a role select
 * and a remove button, and a row adds a person from `candidates`; otherwise the list is read-only.
 * Pure presentation: the container sends the writes and redraws from the server's answer.
 */

import { useId, useState } from "react";
import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Avatar } from "../../../shared/ui/atoms/Avatar";
import { Badge } from "../../../shared/ui/atoms/Badge";
import { Button } from "../../../shared/ui/atoms/Button";
import { Icon } from "../../../shared/ui/atoms/Icon";
import { Select } from "../../../shared/ui/atoms/Select";
import type { MockupRole } from "../../../types/api";
import styles from "./AccessList.module.css";

export type AssignableRole = Exclude<MockupRole, "owner">;

export interface AccessRow {
  readonly userId: string;
  readonly name: string;
  readonly email: string;
  readonly role: MockupRole;
}

export interface AccessPerson {
  readonly id: string;
  readonly name: string;
}

export interface AccessListProps {
  readonly rows: readonly AccessRow[];
  readonly candidates: readonly AccessPerson[];
  readonly editable: boolean;
  /** A personal mockup: only the owner, and the sentence that says so (default P4-5). */
  readonly personal: boolean;
  /** Whether the reader owns it: "Solo tú tienes acceso." only then. */
  readonly viewerOwns: boolean;
  readonly busy: boolean;
  readonly error?: string | undefined;
  readonly onRole: (userId: string, role: AssignableRole) => void;
  readonly onRemove: (userId: string) => void;
  readonly onAdd: (userId: string, role: AssignableRole) => void;
  readonly onTransfer?: (() => void) | undefined;
}

function asRole(value: string): AssignableRole {
  return value === "viewer" ? "viewer" : "editor";
}

export function AccessList({
  rows,
  candidates,
  editable,
  personal,
  viewerOwns,
  busy,
  error,
  onRole,
  onRemove,
  onAdd,
  onTransfer,
}: AccessListProps): ReactElement {
  const { t } = useTranslation();
  const ids = useId();
  const [who, setWho] = useState<string>("");
  const [role, setRole] = useState<AssignableRole>("editor");
  const roleOptions = [
    { value: "editor", label: t("sharing.people.role.editor") },
    { value: "viewer", label: t("sharing.people.role.viewer") },
  ];
  const chosen = candidates.some((person) => person.id === who) ? who : (candidates[0]?.id ?? "");

  return (
    <div>
      <p className={styles.help}>{t("sharing.people.help")}</p>
      {error === undefined ? null : (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
      <ul className={styles.list} aria-label={t("sharing.people.list")}>
        {rows.map((row) => (
          <li key={row.userId} className={styles.row}>
            <Avatar name={row.name} />
            <span className={styles.who}>
              <strong>{row.name}</strong>
              {row.email === "" ? null : <small className={styles.email}>{row.email}</small>}
            </span>
            {row.role === "owner" ? (
              <Badge tone="neutral">{t("sharing.people.owner")}</Badge>
            ) : editable ? (
              <>
                <Select
                  id={`${ids}-role-${row.userId}`}
                  label={t("sharing.people.roleOf", { name: row.name })}
                  value={row.role}
                  options={roleOptions}
                  disabled={busy}
                  onChange={(value) => {
                    onRole(row.userId, asRole(value));
                  }}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  iconOnly
                  label={t("sharing.people.remove", { name: row.name })}
                  disabled={busy}
                  onClick={() => {
                    onRemove(row.userId);
                  }}
                >
                  <Icon name="close" size="sm" />
                </Button>
              </>
            ) : (
              <Badge tone="outline">{row.role === "viewer" ? t("sharing.people.role.viewer") : t("sharing.people.role.editor")}</Badge>
            )}
          </li>
        ))}
      </ul>
      {personal ? (
        <p className={styles.help}>
          {viewerOwns ? t("sharing.people.personal") : t("sharing.people.personalOther")}
        </p>
      ) : (
        <>
          {editable ? (
            candidates.length === 0 ? (
              <p className={styles.help}>{t("sharing.people.allIn")}</p>
            ) : (
              <div className={styles.add}>
                <Select
                  id={`${ids}-who`}
                  label={t("sharing.people.addWho")}
                  value={chosen}
                  options={candidates.map((person) => ({ value: person.id, label: person.name }))}
                  disabled={busy}
                  onChange={setWho}
                />
                <Select
                  id={`${ids}-add-role`}
                  label={t("sharing.people.addRole")}
                  value={role}
                  options={roleOptions}
                  disabled={busy}
                  onChange={(value) => {
                    setRole(asRole(value));
                  }}
                />
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={busy || chosen === ""}
                  onClick={() => {
                    onAdd(chosen, role);
                  }}
                >
                  <Icon name="user-plus" size="sm" />
                  {t("sharing.people.add")}
                </Button>
              </div>
            )
          ) : null}
          <p className={styles.help}>
            {t("sharing.people.roles")}
            {editable && onTransfer !== undefined ? (
              <>
                {" "}
                {t("sharing.people.transferLead")}{" "}
                <button type="button" className={styles.link} onClick={onTransfer}>
                  {t("sharing.people.transfer")}
                </button>
              </>
            ) : null}
          </p>
        </>
      )}
    </div>
  );
}
